{
  "version": "1.1.0",
  "description": "Arcis Core Security Patterns - Language Agnostic",
  "_pattern_convention": {
    "description": "Pattern field naming convention for SDK implementers",
    "pattern": "The primary regex pattern. May contain ReDoS-vulnerable constructs.",
    "pattern_safe": "ReDoS-safe alternative pattern. SDKs MUST prefer this when available.",
    "redos_safe": "Boolean indicating if 'pattern' is ReDoS-safe. If false, use 'pattern_safe'.",
    "usage": "pattern_str = rule.get('pattern_safe') or rule.get('pattern')"
  },
  "config": {
    "max_input_size": 1000000,
    "max_recursion_depth": 10,
    "enable_redos_protection": true
  },
  "patterns": {
    "xss": {
      "name": "Cross-Site Scripting (XSS)",
      "severity": "critical",
      "owasp": "A03:2021",
      "rules": [
        {
          "id": "xss-script-block",
          "pattern": "<script[^>]*>[\\s\\S]*?</script>",
          "flags": "gi",
          "description": "Detects full script blocks (open tag through close tag)",
          "redos_safe": true
        },
        {
          "id": "xss-script-tag",
          "pattern": "<script[^>]*>",
          "flags": "gi",
          "description": "Detects standalone or unclosed script tags",
          "redos_safe": true
        },
        {
          "id": "xss-javascript-protocol",
          "pattern": "javascript\\s*:[^\\s]",
          "flags": "gi",
          "description": "Detects javascript: protocol (allows whitespace around colon)",
          "redos_safe": true
        },
        {
          "id": "xss-event-handler-quoted",
          "pattern": "(?:[\\s/])on\\w+\\s*=\\s*[\"'][^\"']*[\"']",
          "flags": "gi",
          "description": "Detects inline event handlers with quoted values (e.g. onclick=\"alert(1)\")",
          "redos_safe": true
        },
        {
          "id": "xss-event-handler-unquoted",
          "pattern": "(?:[\\s/])on\\w+\\s*=\\s*[^\\s>]*",
          "flags": "gi",
          "description": "Detects inline event handlers with unquoted values (e.g. onload=value)",
          "redos_safe": true
        },
        {
          "id": "xss-iframe-block",
          "pattern": "<iframe[^>]*>[\\s\\S]*?</iframe>",
          "flags": "gi",
          "description": "Detects full iframe blocks",
          "redos_safe": true
        },
        {
          "id": "xss-iframe",
          "pattern": "<iframe[^>]*",
          "flags": "gi",
          "description": "Detects iframe tags (partial or unclosed)",
          "redos_safe": true
        },
        {
          "id": "xss-style-block",
          "pattern": "<style[^>]*>[\\s\\S]*?</style>",
          "flags": "gi",
          "description": "Detects full style blocks (CSS injection, expression(), behavior: attacks)",
          "redos_safe": true
        },
        {
          "id": "xss-style-tag",
          "pattern": "<style[\\s>][^>]*",
          "flags": "gi",
          "description": "Detects style tags (partial or unclosed). Requires whitespace or > after `style` so `<styled>` / `<StyledButton>` text is not flagged.",
          "redos_safe": true
        },
        {
          "id": "xss-object-block",
          "pattern": "<object[^>]*>[\\s\\S]*?</object>",
          "flags": "gi",
          "description": "Detects full object blocks",
          "redos_safe": true
        },
        {
          "id": "xss-object",
          "pattern": "<object[^>]*",
          "flags": "gi",
          "description": "Detects object tags (partial or unclosed)",
          "redos_safe": true
        },
        {
          "id": "xss-embed",
          "pattern": "<embed[^>]*",
          "flags": "gi",
          "description": "Detects embed tags",
          "redos_safe": true
        },
        {
          "id": "xss-data-uri",
          "pattern": "data\\s*:\\s*(?:text/html|image/svg)[^>\\s]*",
          "flags": "gi",
          "description": "Detects data: URIs with HTML or SVG content (avoids 'metadata' false positives)",
          "redos_safe": true
        },
        {
          "id": "xss-vbscript",
          "pattern": "vbscript\\s*:",
          "flags": "gi",
          "description": "Detects vbscript: protocol",
          "redos_safe": true
        },
        {
          "id": "xss-url-encoded",
          "pattern": "%3Cscript",
          "flags": "gi",
          "description": "Detects URL-encoded script tags",
          "redos_safe": true
        },
        {
          "id": "xss-svg-onload",
          "pattern": "<svg[^>]*onload[^>]*>",
          "flags": "gi",
          "description": "Detects SVG with inline event handlers",
          "redos_safe": true
        },
        {
          "id": "xss-form-injection",
          "pattern": "<form[\\s>][^>]*",
          "flags": "gi",
          "description": "Detects form tags used for phishing/credential harvesting via action= redirection",
          "redos_safe": true
        },
        {
          "id": "xss-meta-injection",
          "pattern": "<meta[\\s>][^>]*",
          "flags": "gi",
          "description": "Detects meta tags used for http-equiv refresh redirects or CSP bypass",
          "redos_safe": true
        },
        {
          "id": "xss-base-injection",
          "pattern": "<base[\\s>][^>]*",
          "flags": "gi",
          "description": "Detects base href hijacking — redirects all relative URLs to attacker domain",
          "redos_safe": true
        },
        {
          "id": "xss-link-injection",
          "pattern": "<link[\\s>][^>]*",
          "flags": "gi",
          "description": "Detects link tag injection used for stylesheet or preload CSRF attacks",
          "redos_safe": true
        },
        {
          "id": "xss-css-expression",
          "pattern": "expression\\s*\\(",
          "flags": "gi",
          "description": "Legacy IE CSS expression() in a style context, executes JS. Benchmark xss-style-expression.",
          "redos_safe": true
        }
      ],
      "encoding": {
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        "\"": "&quot;",
        "'": "&#x27;"
      }
    },
    "sql_injection": {
      "name": "SQL Injection",
      "severity": "critical",
      "owasp": "A03:2021",
      "rules": [
        {
          "id": "sqli-keywords",
          "pattern": "(\\bUNION\\s+(?:ALL\\s+)?SELECT\\b)|(\\b(?:DROP|TRUNCATE)\\s+(?:TABLE|DATABASE|INDEX|VIEW|SCHEMA)\\b)|(\\bINTO\\s+(?:OUTFILE|DUMPFILE)\\b)|(\\bATTACH\\s+DATABASE\\b)|(\\bCREATE\\s+(?:USER|FUNCTION|TRIGGER|PROCEDURE)\\b)|(\\bGRANT\\s+(?:ALL|SELECT|INSERT|UPDATE|DELETE)\\b)|(\\bSHUTDOWN\\b)|(\\bxp_cmdshell\\b)|(\\bsp_executesql\\b)",
          "flags": "gi",
          "description": "Multi-token SQL attack shapes (UNION SELECT, DROP TABLE, INTO OUTFILE, etc.) that real attackers use and benign users essentially never type. Replaces older bare-keyword rule that false-positived on 'please select an option', 'I'll update you tomorrow'. Benchmark FP class B3, 2026-06-07.",
          "redos_safe": true,
          "request_boundary_safe": true
        },
        {
          "id": "sqli-comments",
          "pattern": "((?:^|[^!])--(?:[\\s-]|$)|/\\*|\\*/)",
          "flags": "g",
          "description": "SQL comments. ANSI -- requires a following space / dash / end-of-string so it matches a real trailing comment (1=1--, -- -) but not CLI flags (--max-retries). C-style /* */ kept. MySQL # excluded (see sqli-comment-quote). FPR pass 2026-06-08. The -- must not be immediately preceded by '!' so HTML comments (<!-- -->) are not flagged as SQL.",
          "redos_safe": true
        },
        {
          "id": "sqli-boolean-numeric",
          "pattern": "\\bOR\\s+\\d+\\s*=\\s*\\d+",
          "flags": "gi",
          "description": "Detects OR 1=1 style injection",
          "redos_safe": true
        },
        {
          "id": "sqli-boolean-string",
          "pattern": "\\bOR\\s+['\"][^'\"]*['\"]\\s*=\\s*['\"][^'\"]*['\"]?",
          "flags": "gi",
          "description": "Detects OR 'a'='a' style injection",
          "redos_safe": true
        },
        {
          "id": "sqli-and-numeric",
          "pattern": "\\bAND\\s+\\d+\\s*=\\s*\\d+",
          "flags": "gi",
          "description": "Detects AND 1=1 style injection",
          "redos_safe": true
        },
        {
          "id": "sqli-and-string",
          "pattern": "\\bAND\\s+['\"][^'\"]*['\"]\\s*=\\s*['\"][^'\"]*['\"]?",
          "flags": "gi",
          "description": "Detects AND 'a'='a' style injection",
          "redos_safe": true
        },
        {
          "id": "sqli-sleep",
          "pattern": "\\bSLEEP\\s*\\(\\s*\\d+\\s*\\)",
          "flags": "gi",
          "description": "Detects time-based blind SQLi via SLEEP",
          "redos_safe": true
        },
        {
          "id": "sqli-benchmark",
          "pattern": "\\bBENCHMARK\\s*\\(",
          "flags": "gi",
          "description": "Detects time-based blind SQLi via BENCHMARK",
          "redos_safe": true
        },
        {
          "id": "sqli-pg-sleep",
          "pattern": "\\bpg_sleep\\s*\\(",
          "flags": "gi",
          "description": "Detects time-based blind SQLi via PostgreSQL pg_sleep",
          "redos_safe": true
        },
        {
          "id": "sqli-waitfor-delay",
          "pattern": "\\bWAITFOR\\s+DELAY\\b",
          "flags": "gi",
          "description": "Detects time-based blind SQLi via MSSQL WAITFOR DELAY",
          "redos_safe": true
        },
        {
          "id": "sqli-oracle-dbms-packages",
          "pattern": "\\bDBMS_(?:LOCK|PIPE|UTILITY|XSLPROCESSOR|JAVA|OUTPUT|SCHEDULER)\\b",
          "flags": "gi",
          "description": "Detects Oracle DBMS_* package references used in time-based blind SQLi (DBMS_LOCK.SLEEP, DBMS_PIPE.RECEIVE_MESSAGE) and other Oracle stdlib abuse paths. Broad match — no legitimate user input contains these (improvements.md §1.1.e Q3).",
          "redos_safe": true
        },
        {
          "id": "sqli-hex-blob",
          "pattern": "\\b0x[0-9a-fA-F]{16,}\\b",
          "flags": "gi",
          "description": "Long hex-encoded blob (8+ bytes) smuggling SQL past keyword filters; far longer than any hex color. Benchmark sql-hex-encoded.",
          "redos_safe": true,
          "request_boundary_safe": true
        },
        {
          "id": "sqli-comment-quote",
          "pattern": "'\\s*#",
          "flags": "g",
          "description": "Single-quote immediately followed by a MySQL # line comment (admin' #). Quote anchor avoids FP on bare # (hex colors, hashtags). Benchmark sql-comment-hash.",
          "redos_safe": true,
          "request_boundary_safe": true
        }
      ]
    },
    "nosql_injection": {
      "name": "NoSQL Injection",
      "severity": "critical",
      "owasp": "A03:2021",
      "rules": [
        {
          "id": "nosql-operators",
          "pattern": "\\$(?:gt|gte|lt|lte|ne|eq|in|nin|and|or|not|nor|exists|type|regex|where|expr|mod|text|jsonSchema|function|accumulator|elemMatch|all|size|lookup|match|project|group|sort|limit|skip|unwind|addFields|replaceRoot)\\b",
          "flags": "i",
          "description": "Detects MongoDB operators",
          "redos_safe": true
        }
      ],
      "dangerous_keys": [
        "$gt",
        "$gte",
        "$lt",
        "$lte",
        "$ne",
        "$eq",
        "$in",
        "$nin",
        "$and",
        "$or",
        "$not",
        "$nor",
        "$exists",
        "$type",
        "$regex",
        "$where",
        "$expr",
        "$mod",
        "$text",
        "$jsonSchema",
        "$function",
        "$accumulator",
        "$elemMatch",
        "$all",
        "$size",
        "$lookup",
        "$match",
        "$project",
        "$group",
        "$sort",
        "$limit",
        "$skip",
        "$unwind",
        "$addFields",
        "$replaceRoot"
      ]
    },
    "command_injection": {
      "name": "Command Injection",
      "severity": "critical",
      "owasp": "A03:2021",
      "rules": [
        {
          "id": "cmdi-shell-chars",
          "pattern": "[;&|]\\s*(?:(?:cat|ls|dir|rm|cp|mv|wget|curl|nc|ncat|bash|sh|zsh|ksh|chmod|chown|kill|ps|id|touch|ping|dig|su|head|tail|php|sed|whoami|nslookup|nmap|python3?|perl|ruby|node|eval|exec|sudo|telnet|ssh|ftp|tftp|scp|awk|xxd)\\b|base64(?:\\s|$))",
          "flags": "g",
          "description": "Shell command chained after a metacharacter (; cat, | nc, & curl). Replaces the bare [;&|`] rule, which flagged every semicolon / ampersand in code, URLs and prose. FPR pass 2026-06-08. base64 requires a trailing space or end-of-input so `;base64 -d` is caught but the data-URI `;base64,` MIME parameter is not (FP on inline images).",
          "redos_safe": true
        },
        {
          "id": "cmdi-command-substitution",
          "pattern": "\\$\\(",
          "flags": "g",
          "description": "Detects command substitution syntax $( — paired form, fewer false positives than bare parens",
          "redos_safe": true
        },
        {
          "id": "cmdi-ifs-bypass",
          "pattern": "\\$\\{IFS(?:%[^}]*)?\\}",
          "flags": "g",
          "description": "Detects POSIX shell IFS substitution like ${IFS} or ${IFS%??}. Attackers use this to inject spaces past metacharacter filters in commands like `;cat${IFS}/etc/passwd` (improvements.md §1.1.e Q5).",
          "redos_safe": true
        },
        {
          "id": "cmdi-control-chars",
          "pattern": "%0[0-9a-fA-F]",
          "flags": "gi",
          "description": "Detects URL-encoded control characters (%00-%0F) including null, tab, vertical tab, form feed, LF, CR",
          "redos_safe": true
        },
        {
          "id": "cmdi-redirection",
          "pattern": "(>>|<<<?)",
          "flags": "g",
          "description": "Append (>>) and here-doc (<<, <<<) redirection. The bare [<>] space form was dropped (matched 'a > b', 'RAM > 16GB'); redirect-to-file is covered by cmdi-redirect-syspath. FPR pass 2026-06-08.",
          "redos_safe": true
        },
        {
          "id": "cmdi-env-assign",
          "pattern": "\\b(?:LD_PRELOAD|LD_LIBRARY_PATH|BASH_ENV|PYTHONPATH|PERL5LIB|DYLD_INSERT_LIBRARIES)\\s*=",
          "flags": "gi",
          "description": "Dangerous env-var assignment to smuggle code into a spawned process (LD_PRELOAD=). Benchmark cmd-env-var-smuggling.",
          "redos_safe": true
        },
        {
          "id": "cmdi-redirect-syspath",
          "pattern": ">\\s*/(?:etc|var|tmp|usr|bin|sbin|root|home|dev|proc|opt)\\b",
          "flags": "g",
          "description": "Shell output redirect to a system directory (> /var/www/shell.php). Anchored to system dirs to avoid math/text FP. Benchmark cmd-redirect-overwrite.",
          "redos_safe": true
        },
        {
          "id": "cmdi-newline-command",
          "pattern": "[\\n\\r]\\s*(?:cat|ls|dir|rm|cp|mv|wget|curl|nc|ncat|bash|sh|zsh|ksh|chmod|chown|kill|ps|id|touch|ping|dig|su|head|tail|php|sed|whoami|nslookup|nmap|python3?|perl|ruby|node|eval|exec|sudo|telnet|ssh|ftp|tftp|scp|awk|xxd|base64)\\b",
          "flags": "gi",
          "description": "Newline followed by a shell command (host\\ncat /etc/passwd). Benchmark cmd-newline-injection.",
          "redos_safe": true
        },
        {
          "id": "cmdi-backtick-command",
          "pattern": "`\\s*(?:cat|ls|dir|rm|cp|mv|wget|curl|nc|ncat|bash|sh|zsh|ksh|chmod|chown|kill|ps|id|touch|ping|dig|su|head|tail|php|sed|whoami|nslookup|nmap|python3?|perl|ruby|node|eval|exec|sudo|telnet|ssh|ftp|tftp|scp|awk|xxd|base64)\\b",
          "flags": "gi",
          "description": "Backtick command substitution wrapping a known command (`whoami`). Avoids FP on markdown inline code like `npm install`. FPR pass 2026-06-08.",
          "redos_safe": true
        },
        {
          "id": "cmdi-jndi-lookup",
          "pattern": "\\$\\{jndi:",
          "flags": "gi",
          "description": "JNDI lookup used by Log4Shell, e.g. ${jndi:ldap://host/a}",
          "redos_safe": true
        },
        {
          "id": "cmdi-ssi-directive",
          "pattern": "<!--\\s*#\\s*(?:exec|include|echo|config|fsize|flastmod|printenv)\\b",
          "flags": "gi",
          "description": "Server-side include (SSI) directive, e.g. <!--#exec cmd=...-->",
          "redos_safe": true
        }
      ]
    },
    "path_traversal": {
      "name": "Path Traversal",
      "severity": "high",
      "owasp": "A01:2021",
      "rules": [
        {
          "id": "path-dotdot",
          "pattern": "\\.\\./",
          "flags": "g",
          "description": "Detects ../ sequences",
          "redos_safe": true
        },
        {
          "id": "path-dotdot-backslash",
          "pattern": "\\.\\.\\\\",
          "flags": "g",
          "description": "Detects ..\\ sequences",
          "redos_safe": true
        },
        {
          "id": "path-encoded",
          "pattern": "%2e%2e",
          "flags": "gi",
          "description": "Detects URL-encoded traversal",
          "redos_safe": true
        },
        {
          "id": "path-double-encoded",
          "pattern": "%252e",
          "flags": "gi",
          "description": "Detects double URL-encoded traversal",
          "redos_safe": true
        },
        {
          "id": "path-double-encoded-slash",
          "pattern": "%252f",
          "flags": "gi",
          "description": "Detects double URL-encoded forward slash",
          "redos_safe": true
        },
        {
          "id": "path-dotdotslash-bypass",
          "pattern": "\\.{2,}[/\\\\]{2,}",
          "flags": "g",
          "description": "Detects ....// and ....\\\\ bypass variants",
          "redos_safe": true
        },
        {
          "id": "path-null-byte",
          "pattern": "%00",
          "flags": "gi",
          "description": "Detects URL-encoded null byte injection",
          "redos_safe": true
        },
        {
          "id": "path-null-byte-literal",
          "pattern": "\\x00",
          "flags": "g",
          "description": "Detects literal NUL byte in path inputs",
          "redos_safe": true
        },
        {
          "id": "path-mixed-encoded",
          "pattern": "\\.\\.%2[fF]",
          "flags": "g",
          "description": "Detects mixed encoding: literal `..` followed by URL-encoded slash (%2F). Benchmark B6 — Python had no coverage; Node had this pattern. 2026-06-07.",
          "redos_safe": true
        },
        {
          "id": "path-mixed-dot-encoded",
          "pattern": "%2e\\.[\\\\/]",
          "flags": "gi",
          "description": "Detects half-encoded traversal: URL-encoded dot (%2e) followed by a literal dot and a slash (`%2e./`). Node caught this; Python/Go did not. Migration 2026-06-13.",
          "redos_safe": true
        },
        {
          "id": "path-mixed-dot-encoded-rev",
          "pattern": "\\.%2e[\\\\/]",
          "flags": "gi",
          "description": "Detects half-encoded traversal: literal dot followed by URL-encoded dot (%2e) and a slash (`.%2e/`). Node caught this; Python/Go did not. Migration 2026-06-13.",
          "redos_safe": true
        },
        {
          "id": "path-overlong-utf8-dot",
          "pattern": "%[Cc]0%[Aa][Ee]",
          "flags": "g",
          "description": "Detects overlong UTF-8 encoding of '.' (%C0%AE). Historic IIS/Apache decoder bypass. Real char `.` is %2E; overlong encoding only appears in evasion attempts. Benchmark B6 gap — neither SDK caught this before. 2026-06-07.",
          "redos_safe": true
        },
        {
          "id": "path-overlong-utf8-slash",
          "pattern": "%[Cc]0%[Aa][Ff]",
          "flags": "g",
          "description": "Detects overlong UTF-8 encoding of forward slash (%C0%AF). Same IIS/Apache decoder bypass class as path-overlong-utf8-dot. Real slash is %2F; overlong form only appears in evasion. 2026-06-13.",
          "redos_safe": true
        },
        {
          "id": "path-windows-unc",
          "pattern": "\\\\\\\\[A-Za-z0-9_.-]+\\\\",
          "flags": "g",
          "description": "Detects Windows UNC paths (\\\\\\\\server\\\\share) in user input. Legitimate web-app inputs never contain UNC references; attacker UNC payloads leak SMB auth or pull remote payloads. Benchmark B6. 2026-06-07.",
          "redos_safe": true
        }
      ]
    },
    "prototype_pollution": {
      "name": "Prototype Pollution",
      "severity": "high",
      "owasp": "A03:2021",
      "languages": [
        "javascript",
        "typescript"
      ],
      "dangerous_keys": [
        "__proto__",
        "constructor",
        "prototype",
        "__definegetter__",
        "__definesetter__",
        "__lookupgetter__",
        "__lookupsetter__"
      ]
    },
    "ldap_injection": {
      "name": "LDAP Injection",
      "severity": "high",
      "owasp": "A03:2021",
      "rules": [
        {
          "id": "ldap-special",
          "pattern": "[()\\\\*]",
          "flags": "g",
          "description": "Detects LDAP special characters. Broad rule for sanitization context only; NOT safe for request-boundary scanning (every parenthesised string trips it). Use 'ldap-injection-strict' below at request boundary.",
          "redos_safe": true
        },
        {
          "id": "ldap-injection-strict",
          "pattern": "\\)\\s*\\(|\\*\\s*\\)\\s*\\(",
          "flags": "g",
          "description": "Detects attack-specific LDAP filter-break shapes ')(' and '*)('. Safe to wire into request-boundary scanners. Catches '*)(uid=*))(|(uid=*' style payloads.",
          "redos_safe": true,
          "request_boundary_safe": true
        },
        {
          "id": "ldap-not-bypass",
          "pattern": "\\)\\s*\\(\\s*!|&\\s*\\(\\s*!|\\|\\s*\\(\\s*!",
          "flags": "g",
          "description": "Detects LDAP NOT-operator bypass shapes ')(!', '&(!', and '|(!'. Catches injections where attacker appends a NOT clause to enumerate or exclude specific entries, e.g. '*)(uid=*)(!(uid=admin))'. Companion to ldap-injection-strict; together they cover the OR-then-NOT enumeration corpus that legitimate LDAP queries never produce. (improvements.md Q8.)",
          "redos_safe": true,
          "request_boundary_safe": true
        },
        {
          "id": "ldap-null-byte",
          "pattern": "\\\\00|\\x00",
          "flags": "g",
          "description": "LDAP null-byte truncation (admin\\00) used to terminate a filter early and bypass appended constraints. Benchmark ldap-null-byte-truncate.",
          "redos_safe": true,
          "request_boundary_safe": true
        }
      ]
    },
    "xpath_injection": {
      "name": "XPath Injection",
      "severity": "high",
      "owasp": "A03:2021",
      "rules": [
        {
          "id": "xpath-injection-strict",
          "pattern": "('\\s*(or|and)\\s*'|\"\\s*(or|and)\\s*\"|\\)\\s*(or|and)\\s*\\(|\\|\\s*/)",
          "flags": "gi",
          "description": "Detects XPath injection patterns: boolean-injection (' or ' / \" or \"), function-arity tampering with closing+opening parens around or/and, and union operator before path step. Request-boundary safe.",
          "redos_safe": true,
          "request_boundary_safe": true
        },
        {
          "id": "xpath-blind-substring",
          "pattern": "\\bsubstring\\s*\\(\\s*name\\s*\\(|\\bstring-length\\s*\\(|\\bcount\\s*\\(\\s*/",
          "flags": "gi",
          "description": "Blind XPath extraction via substring(name()), string-length(), count(/...). Benchmark xpath-blind-substring.",
          "redos_safe": true,
          "request_boundary_safe": true
        }
      ]
    },
    "email_header_injection": {
      "name": "Email Header Injection (SMTP CRLF)",
      "severity": "high",
      "owasp": "A03:2021",
      "rules": [
        {
          "id": "email-header-smtp-keyword",
          "pattern": "(\\r\\n|\\r|\\n)\\s*(bcc|cc|to|from|subject|reply-to|return-path|content-type|x-mailer)\\s*:",
          "flags": "gi",
          "description": "Detects SMTP header injection: CR/LF followed by a known SMTP header keyword. Narrow enough for request-boundary scanning because legitimate user input rarely contains '\\nBcc:' verbatim.",
          "redos_safe": true,
          "request_boundary_safe": true
        },
        {
          "id": "email-header-bare-newline",
          "pattern": "(\\r\\n|\\r|\\n)\\s*[a-z][a-z0-9-]{0,40}\\s*:",
          "flags": "gi",
          "description": "Detects bare CR/LF followed by ANY plausible header keyword (not just the SMTP allowlist). Catches MTAs that accept bare LF or bare CR as a header separator (qmail, sendmail variants), where attacker bypasses the strict keyword list by injecting a non-SMTP-keyword header (X-Custom, Authentication-Results, DKIM-Signature, ...). Companion to email-header-smtp-keyword; together they cover the bypass class. (improvements.md Q10.)",
          "redos_safe": true,
          "request_boundary_safe": true
        }
      ]
    },
    "ssti": {
      "name": "Server-Side Template Injection (SSTI)",
      "severity": "critical",
      "owasp": "A03:2021",
      "rules": [
        {
          "id": "ssti-jinja2-twig",
          "pattern": "\\{\\{.*?\\}\\}",
          "flags": "g",
          "description": "Detects Jinja2/Twig/Nunjucks template expressions ({{ ... }})",
          "redos_safe": true
        },
        {
          "id": "ssti-freemarker-spring",
          "pattern": "\\$\\{.*?\\}",
          "pattern_safe": "\\$\\{[^}]*[?!()*+\\-/][^}]*\\}",
          "flags": "g",
          "description": "Detects Freemarker/Thymeleaf/Spring EL expressions. Detection uses broad pattern; sanitization uses pattern_safe which requires operators/method-calls to avoid false-positives on JS template literals like ${name}.",
          "redos_safe": true
        },
        {
          "id": "ssti-erb-ejs",
          "pattern": "<%[=\\-]?.*?%>",
          "flags": "gs",
          "description": "Detects ERB/EJS template tags (<%= ... %>, <% ... %>)",
          "redos_safe": true
        },
        {
          "id": "ssti-pug-jade",
          "pattern": "#\\{.*?\\}",
          "pattern_safe": "#\\{[^}]*[?!()*+\\-/][^}]*\\}",
          "flags": "g",
          "description": "Detects Pug/Jade/Slim template expressions. Detection uses broad pattern; sanitization uses pattern_safe to avoid false-positives on Ruby/Pug output like #{name}.",
          "redos_safe": true
        },
        {
          "id": "ssti-python-class-chain",
          "pattern": "__(?:class|mro|subclasses|globals|builtins|import)__",
          "flags": "gi",
          "description": "Detects Python sandbox escape via dunder attributes",
          "redos_safe": true
        },
        {
          "id": "ssti-config-leak",
          "pattern": "\\{\\{\\s*config[.\\[]",
          "flags": "gi",
          "description": "Detects Jinja2 config/settings leak attempts",
          "redos_safe": true
        },
        {
          "id": "ssti-jinja2-functions",
          "pattern": "\\{\\{\\s*(?:self|request|lipsum|cycler|joiner|namespace|range)\\b",
          "flags": "gi",
          "description": "Detects Jinja2 built-in object/function access",
          "redos_safe": true
        },
        {
          "id": "ssti-velocity-ognl",
          "pattern": "#set\\s*\\(\\s*\\$|#foreach\\s*\\(\\s*\\$|\\$\\w+\\.(?:exec|getClass|getRuntime|getMethod|invoke)\\b",
          "flags": "gi",
          "description": "Velocity #set/#foreach directives and OGNL/Velocity method calls. Benchmark ssti-velocity-runtime.",
          "redos_safe": true
        }
      ]
    },
    "xml_injection": {
      "name": "XML/XXE Injection",
      "severity": "critical",
      "owasp": "A03:2021",
      "rules": [
        {
          "id": "xxe-doctype",
          "pattern": "<!DOCTYPE",
          "flags": "gi",
          "description": "Detects DOCTYPE declarations",
          "redos_safe": true
        },
        {
          "id": "xxe-entity",
          "pattern": "<!ENTITY",
          "flags": "gi",
          "description": "Detects ENTITY declarations",
          "redos_safe": true
        },
        {
          "id": "xxe-system",
          "pattern": "SYSTEM\\s+[\"']",
          "flags": "gi",
          "description": "Detects SYSTEM references",
          "redos_safe": true
        }
      ]
    }
  },
  "security_headers": {
    "Content-Security-Policy": "default-src 'self'; script-src 'self'; style-src 'self' 'unsafe-inline'; img-src 'self' data: https:; font-src 'self'; object-src 'none'; frame-ancestors 'none';",
    "X-Content-Type-Options": "nosniff",
    "X-Frame-Options": "DENY",
    "X-XSS-Protection": "0",
    "Strict-Transport-Security": "max-age=31536000; includeSubDomains",
    "Referrer-Policy": "strict-origin-when-cross-origin",
    "Permissions-Policy": "geolocation=(), microphone=(), camera=()",
    "X-Permitted-Cross-Domain-Policies": "none",
    "Cache-Control": "no-store, no-cache, must-revalidate, proxy-revalidate",
    "Pragma": "no-cache",
    "Expires": "0"
  },
  "rate_limiting": {
    "default_max": 100,
    "default_window_ms": 60000,
    "headers": {
      "limit": "X-RateLimit-Limit",
      "remaining": "X-RateLimit-Remaining",
      "reset": "X-RateLimit-Reset"
    }
  },
  "sensitive_keys": [
    "password",
    "passwd",
    "pwd",
    "secret",
    "token",
    "apikey",
    "api_key",
    "apiKey",
    "auth",
    "authorization",
    "credit_card",
    "creditcard",
    "cc",
    "ssn",
    "social_security",
    "private_key",
    "privateKey",
    "access_token",
    "accessToken",
    "refresh_token",
    "refreshToken",
    "bearer",
    "jwt",
    "session",
    "cookie",
    "x-api-key",
    "x-auth-token",
    "credentials"
  ],
  "validation": {
    "email": "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$",
    "url": "^https?://[^\\s/$.?#].[^\\s]*$",
    "uuid": "^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$",
    "ipv4": "^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$"
  }
}
